import { PartialDeep } from 'type-fest';
import validator from 'validator';
import logger from 'loglevel';

import { formatRelative, intervalToDuration, Locale, Duration } from 'date-fns';
// https://github.com/date-fns/date-fns/issues/2964
import { enUS } from 'date-fns/locale/en-US';
import { TrainingSessionTransportState } from './training-session-transport.js';

const isEmail = validator.isEmail;
/**
 * Specific keys cannot not be falsy
 */
export type TruthyPick<T, K extends keyof T> = {
    [P in keyof T]: P extends K ? Exclude<T[P], null | undefined | false | 0 | ''> : T[P];
};

/**
 * Specific keys cannot be nullish (null, undefined)
 */
export type NonNullishPick<T, K extends keyof T> = {
    [P in keyof T]: P extends K ? Exclude<T[P], null | undefined> : T[P];
};

/**
 * Specific keys cannot not be falsy and also not optional
 */
export type RequiredTruthyPick<T, K extends keyof T> = RequiredPick<TruthyPick<T, K>, K>;

/**
 * Specific keys cannot be optional or nullish (null, undefined)
 */
export type RequiredNonNullishPick<T, K extends keyof T> = RequiredPick<NonNullishPick<T, K>, K>;
/**
 * Choose specific properties of a record and force them to be required.
 */
export type RequiredPick<T, K extends keyof T> = Required<Pick<T, K>> & T;

/**
 * Choose specific properties of a record and force them to be nullable.
 */
export type NullablePick<T, K extends keyof T> = {
    [P in keyof T]: P extends K ? T[P] | null : T[P];
};

/**
 * Make all properties of T nullable
 */
export type AllNullable<T> = {
    [P in keyof T]: T[P] | null;
};

/**
 * Get the boolean difference between two types.
 */
export type Diff<T, Y> = {
    [P in keyof T]: P extends keyof Y ? never : T[P];
};

/**
 * Flatten array of arrays (use with arr.reduce(flatten))
 * @deprecated use Array.prototype.flat()
 * @param a array1
 * @param b array2
 * @param _n index
 * @param _v this
 * @returns flattened array with the contents of a and b concatenated.
 */
export function flatten<T>(a: T[], b: T[], _n: number, _v: T[][]): T[] {
    return a.concat(b);
}

export function arrayCompare<T extends number | string | boolean>(a: T[], b: T[]): boolean;
export function arrayCompare<T>(
    a: T[],
    b: T[],
    itemComparator?: (itemA: T, itemB: T) => boolean
): boolean;
export function arrayCompare<T>(a: T[], b: T[], itemComparator?: (itemA: T, itemB: T) => boolean) {
    return (
        a.length === b.length &&
        a.every((_m, i) => (itemComparator ? itemComparator(b[i], a[i]) : b[i] === a[i]))
    );
}

/**
 * Compare two records and return true if all keys are equal between them
 * @param a First object
 * @param b Second object
 * @returns True if both objects have all the same keys and values.
 */
export function objectCompare<T extends Record<string, any>>(a: T, b: T) {
    const aKeys = Object.keys(a);
    return aKeys.length === Object.keys(b).length && aKeys.every(k => k in b && a[k] === b[k]);
}

export type ArrayPropertiesOf<T> = { [P in keyof T]?: T[P] extends Array<unknown> ? T[P] : never };
/**
 * Merge the keys and values from any array.
 * @param items Array of values
 * @param keys Strings referring to the keys of the values
 * @returns Merged array
 */
export function concatKeysFrom<T>(items: T[], keys: Array<keyof ArrayPropertiesOf<T>>) {
    const result = {} as any;
    for (const k of keys) {
        const values = [];
        for (const item of items) {
            const value = item[k] as any;
            if (value) {
                values.push(...value);
            }
        }
        if (values.length) {
            result[k] = values;
        }
    }
    return result as T;
}

export function mergeKeysFrom<T>(items: T[], keys: Array<keyof ArrayPropertiesOf<T>>) {
    const result = {} as any;
    for (const k of keys) {
        const merged = Object.assign({}, ...items.map(i => i[k]));
        if (!isEmpty(merged)) {
            result[k] = merged;
        }
    }
    return result as Partial<T>;
}

/**
 * Pluck an array property off a series of objects and merge the arrays together. Use the idSelector to determine
 * uniqueness, and only retain the last unique item or each unique identifier.
 * Item order is preserved by placing merged items at the index of the first item.
 * @param items Array of values
 * @param selector Selector function to pluck an value off of each item of items.
 * @param idSelector Selector that picks a unique id off of each object. Only the last object with that id will be retained.
 * @param allowEmpty When false an empty array result will be converted to undefined.
 * @returns Merged array or undefined if selector never returned any items
 */
export function concatLatestFrom<T, V, K>(
    items: T[],
    selector: (item: T) => V[] | undefined,
    idSelector: (obj: V) => K,
    allowEmpty = false
) {
    return mergeLatestFrom(items, selector, idSelector, null, allowEmpty, true);
}

/**
 * Pluck an array property off a series of objects and merge the arrays together. Use the idSelector to determine
 * uniqueness, and merge matching items together using the merge function
 * Item order is preserved by placing merged items at the index of the second item
 *  (unless useFirstItemPlacement is true)
 * @param items Array of values
 * @param selector Selector function to pluck an value off of each item of items.
 * @param idSelector Selector that picks a unique id off of each object. Only the last object with that id will be retained.
 * @param merge Merge function to combine two items together.
 * @param allowEmpty When false an empty array result will be converted to undefined.
 * @param useFirstItemPlacement If true then items will remain in the original insertion order of
 *  the first merge item when merged. Otherwise merges will be added at the end of the item order.
 *
 * @returns Merged array or undefined if selector never returned any items
 */
export function mergeLatestFrom<T, V, K>(
    items: T[],
    selector: (item: T) => V[] | undefined,
    idSelector: (obj: V) => K,
    merge: ((prev: V, next: V) => V) | null = null,
    allowEmpty = false,
    useFirstItemPlacement = false
) {
    const uniqueValues = new Map<K, V>();
    for (const item of items) {
        const values = selector(item);
        if (values) {
            for (const value of values) {
                const key = idSelector(value);
                const oldValue = merge && uniqueValues.get(key);
                const newValue = oldValue && merge ? merge(oldValue, value) : value;
                if (!useFirstItemPlacement) {
                    // maps retain insertion order...
                    // sometimes the item should be inserted at the end, other times we want to
                    // retain the original order...
                    uniqueValues.delete(key);
                }
                uniqueValues.set(key, newValue);
            }
        }
    }
    return uniqueValues.size || allowEmpty ? Array.from(uniqueValues.values()) : undefined;
}

/**
 * Call Object.freeze on an object and all it's properties recursively.
 * @param object Object to be frozen.
 */
export function deepFreeze<T extends NonNullable<object>>(object: T): T {
    for (const v of Array.isArray(object) ? object : Object.values(object)) {
        if (v && typeof v === 'object') {
            deepFreeze(v);
        }
    }
    Object.freeze(object);
    return object;
}

/**
 * Deep clone an object. Believe it or not this is 5x faster than deepFreeze and _.cloneDeep
 * and 10x faster than json.stringify/parse
 * https://www.measurethat.net/Benchmarks/Show/29349/0/deepclone-vs-deepfreeze-vs-saferdeepclone2
 * @param object Object to be cloned. Should only be plain objects/arrays/primitives, no maps etc.
 * @returns Object that is a deep clone of the input.
 */
export function deepClone<T extends string | number | object | Array<unknown>>(object: T): T {
    // null ?
    if (!object) {
        return object;
    }
    if (typeof object !== 'object') {
        // this is not an array or an object, it's a primitive...
        // having a type system doesn't 100% prevent this...
        return object;
    }
    // also.. hopefully nobody ever uses `new String('a')` or `new Number(1)`
    // but protect against that edge case anyways..
    if (typeof object.valueOf() !== 'object') {
        return new (object.constructor as { new (t: unknown): T })(object.valueOf());
    }
    if (Array.isArray(object)) {
        const len = object.length;
        const result = new Array(len);
        for (let i = 0; i < len; ++i) {
            const v = object[i];
            result[i] = v && typeof v === 'object' ? deepClone(v) : v;
        }
        return result as T;
    } else {
        const result: Record<any, any> = {};
        for (const k in object) {
            const v = object[k];
            result[k] = v && typeof v === 'object' ? deepClone(v) : v;
        }
        return result;
    }
}

/**
 * Create a partialdeep of the object that can be re-integrated later by deepApply against
 * a to create a clone of b.
 * Array<T> will not be diffed since we'd have to convert them to Array<MaybeDeleted<T>>
 * @param a Object to be compared
 * @param b Object which is similar to a
 * @returns partialDeep version containing only the differences between a and b.
 */
export function deepDiff<T extends object>(a: T, b: T): PartialDeep<T> {
    const result: Record<any, any> = {};
    for (const k in b) {
        const av = a[k];
        const bv = b[k];

        if (av && bv && typeof av === 'object' && typeof bv === 'object') {
            if (Array.isArray(av) && Array.isArray(bv)) {
                // TODO: use deepEqual to avoid certain diffs
                // for now, do a shallow identity comparison of all elements
                // and skip this property if they match
                if (!arrayCompare(av, bv)) {
                    result[k] = bv;
                }
            } else {
                const diff = deepDiff(av, bv);
                if (!isEmpty(diff as object)) {
                    result[k] = diff;
                }
            }
        } else {
            if (av !== bv) {
                result[k] = bv;
            }
        }
    }
    return result as PartialDeep<T>;
}

/**
 * Checks for the deep equality between a and b
 * @param a Object to be compared
 * @param b Object which is similar to a
 * @returns true if a and b have deep equality, false otherwise.
 */
export function deepEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; ++i) {
            if (!deepEqual(a[i], b[i])) return false;
        }
        return true;
    } else if (typeof a === 'object' && typeof b === 'object') {
        if (Object.keys(a).length !== Object.keys(b).length) return false;
        for (let key in b) {
            if (!a.hasOwnProperty(key)) return false;
            if (!deepEqual(a[key], b[key])) return false;
        }
        return true;
    }
    return false;
}

/**
 * Check to see if an object has any keys. (very quickly)
 * https://www.measurethat.net/Benchmarks/Show/22599/0/fastest-way-to-check-if-object-is-empty2
 * @param object Object that may be empty
 * @returns True if the object is empty
 */
export function isEmpty(object: object) {
    for (const _ in object) {
        return false;
    }
    return true;
}

/**
 * Apply a difference object created by deepDiff. Additive only, doesn't account for deletion
 * @param a Whole object
 * @param b PartialDeep object with changes
 * @returns a with changes from b.
 */
export function deepApply<Y extends NonNullable<object>, T extends Y>(a: T, b: PartialDeep<Y>): T {
    const result: Record<any, any> = {};
    for (const k in a) {
        if (b && k in b) {
            apply(k);
        } else {
            result[k] = a[k];
        }
    }
    for (const k in b) {
        if (!(k in a)) {
            apply(k as keyof T | keyof PartialDeep<T>);
        }
    }
    return result as T;

    function apply(k: keyof T | keyof PartialDeep<T>) {
        const av = a[k as keyof T];
        const bv = (b as Record<any, any>)[k];
        if (av && bv && typeof av === 'object' && typeof bv === 'object') {
            if (Array.isArray(bv)) {
                result[k] = bv;
            } else {
                result[k] = deepApply(av, bv);
            }
        } else {
            result[k] = bv;
        }
    }
}

/**
 * Split an array into chunkSize sized chunks. Pass to [].reduce(chunk([size]), [])
 * @param items
 * @param size
 */
export function chunk<T>(chunkSize = 50) {
    return (chunks: T[][], item: T) => {
        let current = chunks[chunks.length - 1];
        if (!current || current.length >= chunkSize) {
            chunks.push((current = []));
        }
        current.push(item);
        return chunks;
    };
}

export function groupBy<T, K>(items: T[], keySelector: (item: T) => K): Map<K, T[]> {
    return items.reduce((m, item) => {
        const key = keySelector(item);
        const groupItems = m.get(key);
        if (groupItems === undefined) {
            m.set(key, [item]);
        } else {
            groupItems.push(item);
        }
        return m;
    }, new Map());
}

export function lastUnique<T, K>(items: T[], keySelector: (item: T) => K): T[] {
    return Array.from(
        items
            .reduce((m, item) => {
                const key = keySelector(item);
                m.set(key, item);
                return m;
            }, new Map())
            .values()
    );
}

export function indexBy<T, K>(items: T[], keySelector: (item: T, i: number) => K): Map<K, T> {
    return items.reduce((m, item, i) => {
        const key = keySelector(item, i);
        m.set(key, item);
        return m;
    }, new Map());
}

export function invertObjectKeyValues<
    K extends string | number | symbol,
    V extends string | number | symbol
>(obj: Record<K, V>): Record<V, K> {
    return Object.entries(obj).reduce(
        (r, [k, v]) => {
            r[v as V] = k as K;
            return r;
        },
        {} as Record<V, K>
    );
}

export function siUnitFormat(value: number, baseUnit = 'B') {
    const siSuffixes = ['Gi', 'Mi', 'Ki'];
    let unit: string = '';
    while (value > 1024 && siSuffixes.length) {
        value /= 1024;
        unit = siSuffixes.pop()!;
    }
    unit += baseUnit;
    return { value, unit };
}

function flbFormatDuration(duration: Duration) {
    // skip any parts that are 0 or '', keep any that are '0'
    const daysPlus = [
        [duration.years, 'y'],
        [duration.months, 'm'],
        [duration.days, 'd']
    ].filter(([value]) => !!value);
    if (daysPlus.length) {
        // add a space if there are any units greater than hours
        daysPlus.push([' ', '']);
    }
    return [
        ...daysPlus,
        [duration.hours || '0', ':'],
        [String(duration.minutes || 0).padStart(2, '0'), ':'],
        [String(duration.seconds || 0).padStart(2, '0'), '']
    ]
        .map(parts => parts.join(''))
        .join('');
}

export function formatBonusDuration(started: number, completed: number) {
    return flbFormatDuration(
        intervalToDuration({
            start: started,
            end: completed
        })
    );
}

export function formatBonusTimestamp(
    timestampMs: number,
    trainingSession?: TrainingSessionTransportState
) {
    const date = new Date(timestampMs);
    if (trainingSession?.start) {
        const duration = intervalToDuration({
            start: new Date(trainingSession.start),
            end: date
        });
        return flbFormatDuration(duration);
    } else {
        const locale: Locale = {
            ...enUS,
            formatRelative(token: any, date: any, baseDate: any) {
                // get rid of 'today at' for today's times
                const result = token == 'today' ? 'p' : enUS.formatRelative!(token, date, baseDate);
                // make it slightly more verbose and ensure that the time always gets shown
                return result + result[result.length - 1] + (result.endsWith('p') ? '' : 'pp');
            }
        };
        return formatRelative(date, Date.now(), { locale });
    }
}

export function elapsed(ts: number | string, minimum = 10000) {
    if (typeof ts === 'string') {
        ts = new Date(ts).getTime();
    }
    const elapsedMs = Date.now() - ts;
    if (typeof ts === 'number' && elapsedMs < minimum) {
        return 'now';
    } else if (!ts) {
        return 'unknown';
    }
    const result = humanTimeInterval(elapsedMs);
    return result ? `${result} ago` : 'now';
}

export function humanTimeInterval(intervalMs: number, includeSeconds = true) {
    const S = Math.floor(intervalMs / 1000);
    const M = Math.floor(S / 60);
    const H = Math.floor(M / 60);
    const D = Math.floor(H / 24);
    const MSV = 3; // unit of times past 3 seem unnecessary? i.e. DD:HH:mm:ss vs DD:HH:mm
    const result = [
        ['days', D],
        ['hours', H % 24],
        ['minutes', M % 60],
        ...(includeSeconds ? [['seconds', S % 60]] : [])
    ]
        .filter(([_, v]) => Number(v) > 0)
        .map(([k, v]) => `${v} ${k}`)
        .filter((_, index, _arr) => {
            return index < MSV;
        })
        .join(' ');
    return result.trim();
}

export function dateToLocalISOString(ts: Date | number = Date.now()) {
    if (typeof ts !== 'number') {
        ts = ts.getTime();
    }
    const d = new Date(ts);
    const offset = d.getTimezoneOffset();
    const offset1 = p(Math.floor(offset / 60));
    const offset2 = p(offset % 60);
    const sign = offset > 0 ? '-' : '+';
    const ms = d.getMilliseconds();
    const msStr = ms === 0 ? '' : `.${p(ms, 3).replace(/\.?0+$/, '')}`;
    return `${p(d.getFullYear(), 4)}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}${msStr}${sign}${offset1}:${offset2}`;

    function p(n: number, chars = 2) {
        return String(n).padStart(chars, '0');
    }
}

export function base32String(value: number, length = 6) {
    const map: { [index: string]: string } = { 1: 'W', 0: 'X', O: 'Y', I: 'Z' };
    const result = value
        .toString(32)
        .toUpperCase()
        .slice(2, 2 + length)
        .replace(/[01IO]/g, m => map[m]);
    return '0'.repeat(Math.max(0, length - result.length)) + result;
}

/**
 * Generate a human friendly unique (probably) code.
 * See {@link genToken} in the server AuthToken class for a more secure, unique version.
 * @param length Code length. Defaults to the default length of {@link base32String}
 * @returns A human friendly code that should probably be unique. You may need to compare with existing codes if possible.
 */
export function genCode(length?: number) {
    return base32String(Math.random(), length);
}

const flagLetterMap = new Map<string, string>();
export function flagEmoji(code: string) {
    if (flagLetterMap.size === 0) {
        const flag_a = '🇦'.codePointAt(0)!;
        const letter_a = 'A'.codePointAt(0)!;
        const letter_z = 'Z'.codePointAt(0)!;
        for (let i = 0; i <= letter_z - letter_a; ++i) {
            const letter = String.fromCodePoint(letter_a + i);
            const flag = String.fromCodePoint(flag_a + i);
            flagLetterMap.set(letter, flag);
        }
    }
    return code
        ? Array.from(code)
              .map(l => flagLetterMap.get(l))
              .join('') || ''
        : '';
}

export function makeColors(count = 12) {
    const colors: number[] = [];
    const dist = 0x5e;
    count *= dist;
    for (let colorIndex = 1; colorIndex < count; colorIndex += dist) {
        /* eslint-disable  no-bitwise */
        const c = [colorIndex % 0xff & 0xff, ((colorIndex << 4) | (colorIndex >> 4)) & 0xff, 0];
        c[2] = 0xff - (c[0] + c[1]) / ((colorIndex % 3) + 1);
        c.splice(0, 0, ...c.slice(colorIndex % 3));
        const color = c.slice(0, 3).reduce((n, v, channel) => n | (v << (channel * 8)));
        colors.push((color >>> 0) & 0xffffff);
        /* eslint-enable  no-bitwise */
    }
    return colors;
}

export function findLast<T>(items: T[], predicate: (item: T) => boolean): T | undefined {
    for (let i = items.length - 1; i >= 0; --i) {
        if (predicate(items[i])) {
            return items[i];
        }
    }
    return undefined;
}

export function scrubFilename(filename: NonNullable<string>) {
    return filename.replace(/\/|\.\./g, '');
}

export interface SystemError extends Error {
    code: string;
}

export interface PackageJson {
    version: string;
}

/**
 * Create a type guard filter function so that the output type from e.g. Array.prototype.filter()
 * or rxjs filter operator will assume that the requested key is definitely present afterward.
 * @param key Key of the input type to ensure.
 * @returns A type guard predicate that checks if that key is present in the input object.
 */
export function hasKey<T extends Record<string, any>, K extends keyof T>(key: K) {
    return (o: T): o is RequiredPick<T, K> => key in o;
}

/**
 * A type guard filter function which changes the output type after a filter so it does not
 * include any non-truthy type. (e.g. discards null | undefined etc )
 * @param value Value which could be truthy
 * @returns True when the value is truthy. Also acts as a typeguard.
 */
export function truthy<T>(value: T | null | undefined | false | '' | 0): value is T {
    return !!value;
}

/**
 * A type guard filter function which changes the output type after a filter so it does not
 * include any truthy type. (e.g. discards null | undefined etc )
 * @param value Value which could be truthy
 * @returns True when the value is truthy. Also acts as a typeguard.
 */
export function notTruthy<T>(
    value: T | null | undefined | false | '' | 0
): value is null | undefined | false | '' | 0 {
    return !value;
}

/**
 * A type guard filter function which changes the output type after a filter so it does not
 * include any nullish type (undefined/null).
 * @param value Value which could be not nullish
 * @returns True when the value is not nullish. Also acts as a typeguard.
 */
export function notNullish<T>(value: T | null | undefined): value is T {
    return value != null;
}

/**
 * A type guard filter function which changes the output type after a filter so it does not
 * include any nullish type or false (undefined/null/false).
 * @param value Value which could be not nullish or false.
 * @returns True when the value is not nullish or false. e.g. 0 and '' would return true.
 *  Also acts as a typeguard.
 */
export function notNullishOrFalse<T>(value: T | null | undefined | boolean): value is T {
    return value != null && value !== false;
}

/**
 * A type guard filter function which changes the output type after a filter so it does not
 * include any values where value[key] is a nullish type (undefined/null).
 * @param key Key of each value which should not be nullish
 * @returns True when the value is not nullish. Also acts as a typeguard.
 */
export function notNullishKey<T extends Record<string, any>, K extends keyof T>(key: K) {
    return (value: T | null | undefined): value is RequiredNonNullishPick<T, K> =>
        value?.[key] != null;
}

/**
 * Create a type guard filter function so that the output type from e.g. Array.prototype.filter()
 * or rxjs filter operator will assume that the requested key is definitely truthy afterward.
 * @param key Key of the input type to ensure.
 * @returns A type guard predicate that checks if that key is present in the input object.
 */
export function truthyKey<T extends Record<string, any>, K extends keyof T>(key: K) {
    return (o: T): o is RequiredTruthyPick<T, K> => !!o[key];
}

export function assertRequiredKey<T extends Record<string, any>, K extends keyof T>(
    value: T,
    key: K
): asserts value is RequiredPick<T, K> {
    if (!(key in value)) {
        throw new Error(`Key ${String(key)} is required on ${JSON.stringify(value)}`);
    }
}

export function isInstanceOf<T>(cls: new (...args: any[]) => T) {
    return (obj: any): obj is T => obj instanceof cls;
}

export function assertIsError(error: unknown): asserts error is Error {
    if (!isErrorLike(error)) {
        logger.error('Expected value was not an Error-like object: ', error);
        throw error;
    }
}

export function isErrorLike(error: unknown): error is Error {
    const nonNullObject = (err: unknown): err is object => typeof err === 'object' && err !== null;
    const hasStringName = (err: object) => 'name' in err && typeof err.name === 'string';
    const hasStringMsg = (err: object) => 'message' in err && typeof err.message === 'string';
    const maybeStringStack = (err: object) => !('stack' in err) || typeof err.stack === 'string';
    const checkErrorLike = (err: unknown) =>
        nonNullObject(err) && hasStringName(err) && hasStringMsg(err) && maybeStringStack(err);

    // short-circuit the duck-type checking if it's just an Error
    // - instanceof fails if derived type is cross-realm / from a different bundle ref / etc.
    return error instanceof Error || checkErrorLike(error);
}

export function assertInstanceOf<T>(
    obj: unknown,
    cls: new (...args: any[]) => T
): asserts obj is T {
    if (!(obj instanceof cls)) {
        throw new Error(`${obj} is not an instance of ${cls.name}`);
    }
}

export function errorIfNullish<T>(value: T | null | undefined): T {
    assertNotNullish(value);
    return value;
}

export function assertNotNullish<T>(value: T | null | undefined): asserts value is T {
    if (value == null) {
        throw new Error(`Value cannot be nullish: ${JSON.stringify(value) || typeof value}`);
    }
}

export function validateEmail(email: string): boolean {
    return isEmail(email);
}

/**
 * Converts a string into a RegEx safe string by escaping special characters
 * @param string a string to escape for use in RegEx
 * @returns the escaped string
 */
export function escapeRegExp(string: string): string {
    return string.replace(/[$()*+.?[\\\]^{|}]/g, '\\$&'); // $& means the whole matched string
}

/**
 * Adds fe0e variation selector 15 to enforce non-emoji variation of font
 * http://www.unicode.org/reports/tr51/
 */
export function addTextPresentationSelector(unicode?: string): string[] {
    const TEXT_SELECTOR = '\ufe0e';
    const result = [];
    // find one unicode character codepoint
    const charEx = /./gu;
    let match;
    while ((match = charEx.exec(unicode || ''))) {
        result.push(match[0]);
    }
    return result.map(c => `${c}${TEXT_SELECTOR}`);
}

export function csvQuote(value: string) {
    return value.includes(',') ? `"${value.replace(/"/g, '""')}"` : value;
}

export function csvRow(columns: Set<string>, data?: Record<string, unknown>, minPad = 0) {
    // https://en.wikipedia.org/wiki/Comma-separated_values
    const result = [];
    let cwidth = 0;
    if (typeof minPad === 'number' && minPad) {
        cwidth = Array.from(columns).reduce((w, c) => Math.max(w, c.length, minPad), 0);
    }
    for (const name of columns) {
        if (minPad) {
            cwidth = Math.max(minPad, name.length);
        }
        if (data) {
            if (name in data && data[name] != null) {
                let value: string = String(data[name]);
                const numVal = Number(value);
                if (!isNaN(numVal)) {
                    if (name === 'time') {
                        value = new Date(numVal).toISOString();
                    } else if (name !== 'name') {
                        value = String(numVal.toFixed(2));
                    }
                }
                value = csvQuote(value.padStart(cwidth));
                result.push(value);
            } else {
                result.push('?'.padStart(cwidth));
            }
        } else {
            result.push(name.padStart(cwidth));
        }
    }
    return `${result.join(',')}\n`;
}

export function abbreviateLongString(s: string, maxLength: number): string {
    const msgLen = s.length;
    const sumLenStart = Math.floor(maxLength * 0.75);
    const sumLenEnd = Math.floor(maxLength * 0.25);
    return msgLen < maxLength + 3
        ? s
        : `${s.substring(0, sumLenStart)}…${s.slice(msgLen - sumLenEnd)}`;
}

/**
 * Default maximum size for stored webhook responses (10KB)
 */
export const DEFAULT_MAX_WEBHOOK_RESPONSE_SIZE = 1024 * 10;

/**
 * Truncates a response if it exceeds the maximum size.
 * Returns the original response if small enough, or a truncated representation.
 * @param response The response to potentially truncate
 * @param maxSize Maximum size in bytes (defaults to 10KB)
 * @returns The original response or a truncated representation
 */
export function truncateResponse(
    response: unknown,
    maxSize: number = DEFAULT_MAX_WEBHOOK_RESPONSE_SIZE
): unknown {
    if (response === undefined || response === null) {
        return response;
    }
    const str = typeof response === 'string' ? response : JSON.stringify(response);
    if (str.length <= maxSize) {
        return response;
    }
    return {
        _truncated: true,
        message: `Response too large to store (${str.length} bytes, max ${maxSize})`,
        preview: `${str.slice(0, 500)}...`
    };
}
